import { useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Save, Trash2, X, BookOpen, Calendar } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface JournalEntry {
  id: string;
  title: string;
  content: string;
  child_id?: string;
  created_at: string;
  child_profiles?: {
    name: string;
  };
}

interface ChildOption {
  value: string;
  label: string;
}

interface JournalEntryProps {
  entry: JournalEntry;
  childOptions: ChildOption[];
}

export const JournalEntry = ({ entry, childOptions }: JournalEntryProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(entry.title);
  const [editedContent, setEditedContent] = useState(entry.content);
  const [editedChildId, setEditedChildId] = useState<string | undefined>(entry.child_id);

  const handleSaveEdit = async () => {
    if (!editedTitle.trim() || !editedContent.trim()) {
      toast({
        title: "Error",
        description: "Title and content are required",
        variant: "destructive",
      });
      return;
    }

    const { error } = await supabase
      .from('journal_entries')
      .update({
        title: editedTitle,
        content: editedContent,
        child_id: editedChildId,
      })
      .eq('id', entry.id);

    if (error) {
      console.error('Error updating journal entry:', error);
      toast({
        title: "Error",
        description: "Failed to update journal entry",
        variant: "destructive",
      });
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["journalEntries"] });
    setIsEditing(false);
    
    toast({
      title: "Success",
      description: "Journal entry updated successfully",
      duration: 500,
    });
  };

  const handleDeleteEntry = async () => {
    const { error } = await supabase
      .from('journal_entries')
      .delete()
      .eq('id', entry.id);

    if (error) {
      console.error('Error deleting journal entry:', error);
      toast({
        title: "Error",
        description: "Failed to delete journal entry",
        variant: "destructive",
      });
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["journalEntries"] });
    toast({
      title: "Success",
      description: "Journal entry deleted successfully",
      duration: 500,
    });
  };

  return (
    <div className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow duration-200">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-grow">
          {isEditing ? (
            <div className="space-y-4">
              <input
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter title"
              />
              <div className="flex gap-4">
                <div className="w-1/2">
                  <select
                    value={editedChildId || ""}
                    onChange={(e) => setEditedChildId(e.target.value || undefined)}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select child (optional)</option>
                    {childOptions.map(child => (
                      <option key={child.value} value={child.value}>
                        {child.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                <h3 className="text-xl font-semibold">{entry.title}</h3>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                <Calendar className="w-4 h-4" />
                <span>
                  {format(new Date(entry.created_at), "MMM d, yyyy 'at' h:mm a")}
                </span>
                {entry.child_profiles && (
                  <>
                    <span>•</span>
                    <span>{entry.child_profiles.name}</span>
                  </>
                )}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleSaveEdit}
                className="text-green-600 hover:text-green-700 hover:bg-green-50"
              >
                <Save className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsEditing(false);
                  setEditedTitle(entry.title);
                  setEditedContent(entry.content);
                  setEditedChildId(entry.child_id);
                }}
                className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
              >
                <X className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsEditing(true)}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Journal Entry</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this journal entry? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteEntry}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>
      {isEditing ? (
        <Textarea
          value={editedContent}
          onChange={(e) => setEditedContent(e.target.value)}
          className="mt-4"
          placeholder="Write your entry content here..."
        />
      ) : (
        <p className="mt-4 text-gray-700 whitespace-pre-wrap">
          {entry.content}
        </p>
      )}
    </div>
  );
};
